import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Button, CircularProgress, Chip,
  ToggleButtonGroup, ToggleButton, Table, TableBody, TableCell, TableHead, TableRow, IconButton,
} from '@mui/material';
import { FlightTakeoff, Refresh, ChevronLeft, ChevronRight } from '@mui/icons-material';
import axios from 'axios';

const AVIATOR_API = 'https://slots.moonraid.online/api/admin/aviator';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

const Stat = ({ label, value, color, hint }) => (
  <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,255,255,0.06)' }}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="caption" sx={{ color: '#8fa1c4' }}>{label}</Typography>
      <Typography variant="h6" sx={{ color: color || '#fff', fontWeight: 800 }}>{value}</Typography>
      {hint && <Typography variant="caption" sx={{ color: '#5b6b8c' }}>{hint}</Typography>}
    </CardContent>
  </Card>
);

export default function AviatorAdmin() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [period, setPeriod] = useState('today');
  const [sort, setSort] = useState('rtp');
  const [live, setLive] = useState(null);
  const [flights, setFlights] = useState([]);
  const [weekly, setWeekly] = useState(null);
  const [history, setHistory] = useState(null);
  const [histIdx, setHistIdx] = useState(0); // пагинация истории выплат (0 = последняя)
  const histTouch = useRef({ x: 0, y: 0 });  // для свайпа влево/вправо по неделям

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${AVIATOR_API}/stats`, { headers: authHeaders() });
      if (r.data?.success) setStats(r.data);
    } catch (e) { /* тихо */ }
  }, []);

  const loadFlights = useCallback(async () => {
    try {
      const r = await axios.get(`${AVIATOR_API}/flights`, { params: { limit: 50 }, headers: authHeaders() });
      if (r.data?.success) setFlights(r.data.flights);
    } catch (e) { /* тихо */ }
  }, []);

  const loadWeekly = useCallback(async () => {
    try {
      const r = await axios.get(`${AVIATOR_API}/weekly`, { headers: authHeaders() });
      if (r.data?.success) setWeekly(r.data);
    } catch (e) { /* тихо */ }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${AVIATOR_API}/weekly-history`, { headers: authHeaders() });
      if (r.data?.success) { setHistory(r.data); setHistIdx(0); }
    } catch (e) { /* тихо */ }
  }, []);

  const loadPlayers = useCallback(async () => {
    try {
      const r = await axios.get(`${AVIATOR_API}/players`, { params: { period, sort }, headers: authHeaders() });
      if (r.data?.success) setPlayers(r.data.players);
    } catch (e) { /* тихо */ }
  }, [period, sort]);

  const loadLive = useCallback(async () => {
    try {
      const r = await axios.get(`${AVIATOR_API}/live`, { headers: authHeaders() });
      if (r.data?.success) setLive(r.data.state);
    } catch (e) { /* тихо */ }
  }, []);

  useEffect(() => {
    (async () => { setLoading(true); await Promise.all([loadStats(), loadFlights(), loadWeekly(), loadHistory()]); setLoading(false); })();
    const t = setInterval(() => { loadStats(); loadFlights(); loadWeekly(); }, 15000);
    return () => clearInterval(t);
  }, [loadStats, loadFlights, loadWeekly, loadHistory]);

  useEffect(() => { loadPlayers(); }, [loadPlayers]);

  // лайв-обновление текущего раунда каждые 1.5с
  useEffect(() => {
    loadLive();
    const t = setInterval(loadLive, 1500);
    return () => clearInterval(t);
  }, [loadLive]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);
  const num = (v) => (v == null ? '—' : Number(v).toLocaleString('ru-RU'));

  // время полёта по МСК; для не-сегодняшних — с датой
  const fmtFlightTime = (iso) => {
    const d = new Date(iso);
    const msk = { timeZone: 'Europe/Moscow' };
    const t = d.toLocaleTimeString('ru-RU', { ...msk, hour12: false });
    const day = d.toLocaleDateString('ru-RU', msk);
    return day === new Date().toLocaleDateString('ru-RU', msk)
      ? t : `${d.toLocaleDateString('ru-RU', { ...msk, day: '2-digit', month: '2-digit' })} ${t}`;
  };
  const crashColor = (x) => (x < 2 ? '#ff4d5e' : x < 10 ? '#22e0c8' : '#ff7ad1');
  const betResult = (b) => (
    b.status === 'cashed' && b.mult != null ? `забрал ${Number(b.mult).toFixed(2)}×`
    : b.partialMult != null ? `частично ${Number(b.partialMult).toFixed(2)}×`
    : 'не успел'
  );
  // Метка возле ника: D — демо (фейк-монеты), F — фрибет по промокоду.
  // Цвета как в стакане игры: D золотой, F бирюзовый.
  const DFMark = ({ b }) => (
    b?.demo
      ? <Box component="span" sx={{ ml: 0.5, px: 0.5, borderRadius: 0.5, fontSize: 10, fontWeight: 800, lineHeight: 1.4, color: '#f5c945', border: '1px solid #f5c94588', bgcolor: '#f5c94522' }}>D</Box>
      : b?.free
        ? <Box component="span" sx={{ ml: 0.5, px: 0.5, borderRadius: 0.5, fontSize: 10, fontWeight: 800, lineHeight: 1.4, color: '#22e0c8', border: '1px solid #22e0c888', bgcolor: '#22e0c822' }}>F</Box>
        : null
  );

  // ── Недельный лидерборд: формат недели «ДД.ММ–ДД.ММ.ГГ» (пн–вс) ──
  const fmtWeek = (ws) => {
    const start = new Date(`${ws}T00:00:00`);
    const end = new Date(start); end.setDate(end.getDate() + 6);
    const dm = (d) => d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
    return `${dm(start)}–${dm(end)}.${String(end.getFullYear()).slice(2)}`;
  };
  const medal = (place) => (place === 1 ? '🥇' : place === 2 ? '🥈' : place === 3 ? '🥉' : `${place}.`);
  // строка топа лидерборда: место/ник/метрика + прогноз приза для мест 1–3
  const LbRow = ({ r, metric }) => (
    <TableRow>
      <TableCell sx={{ color: r.rank <= 3 ? '#f4c34a' : '#5b6b8c', fontWeight: 700, width: 36 }}>{medal(r.rank)}</TableCell>
      <TableCell sx={{ color: '#fff' }}>{r.name}</TableCell>
      <TableCell align="right" sx={{ color: '#22e0c8', fontWeight: 700 }}>
        {metric === 'mult' ? `${Number(r.value).toFixed(2)}×` : num(r.value)}
      </TableCell>
      <TableCell align="right" sx={{ color: r.prize != null ? '#2bff9a' : '#5b6b8c', fontWeight: r.prize != null ? 700 : 400 }}>
        {r.prize != null ? `+${num(r.prize)}` : '—'}
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <FlightTakeoff sx={{ color: '#22e0c8' }} />
        <Typography variant="h6" sx={{ fontWeight: 800 }}>Авиатор — статистика</Typography>
        <Chip size="small" label="слот №7" sx={{ ml: 1, bgcolor: '#22e0c822', color: '#22e0c8' }} />
        <Button size="small" startIcon={<Refresh />} onClick={() => { loadStats(); loadFlights(); loadWeekly(); loadHistory(); }} sx={{ ml: 'auto' }}>Обновить</Button>
      </Box>

      {/* статистика */}
      {stats && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}><Stat label="Резерв сейчас" value={`${num(stats.reserve)} SPERM`} color="#f4c34a" /></Grid>
          <Grid item xs={6} md={3}><Stat label="Профит за всё время" value={`${num(stats.profitAllTime)}`} color={stats.profitAllTime >= 0 ? '#2bff9a' : '#ff4d5e'} /></Grid>
          <Grid item xs={6} md={3}><Stat label="Профit сегодня" value={`${num(stats.profitToday)}`} color={stats.profitToday >= 0 ? '#2bff9a' : '#ff4d5e'} hint={`${stats.roundsToday} раундов`} /></Grid>
          <Grid item xs={6} md={3}><Stat label="Спинов всего" value={num(stats.rounds)} /></Grid>
          <Grid item xs={6} md={3}><Stat label="RTP факт (окно)" value={pct(stats.actualRtpWindow)} color="#22e0c8" hint="последние 500" /></Grid>
          <Grid item xs={6} md={3}><Stat label="RTP факт (всё время)" value={pct(stats.actualRtpAllTime)} /></Grid>
          <Grid item xs={6} md={3}><Stat label="Поставлено / выплачено" value={`${num(stats.totalBet)} / ${num(stats.totalPaid)}`} /></Grid>
          <Grid item xs={6} md={3}><Stat label="Крупнейший икс" value={`${stats.biggestCrash}×`} color="#ff7ad1" /></Grid>
        </Grid>
      )}

      {/* ЛАЙВ — текущий раунд в реалтайме */}
      <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(34,224,200,0.25)', mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2bff9a', boxShadow: '0 0 8px #2bff9a' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Лайв — текущий раунд</Typography>
            {live && (
              <Chip size="small" label={
                live.phase === 'flying' ? `в полёте ${Number(live.multiplier).toFixed(2)}×`
                : live.phase === 'crashed' ? `улетел ${Number(live.crashed).toFixed(2)}×`
                : live.phase === 'betting' || live.phase === 'locked' ? 'приём ставок' : live.phase
              } sx={{ ml: 1, bgcolor: live.phase === 'crashed' ? '#ff4d5e22' : '#22e0c822', color: live.phase === 'crashed' ? '#ff4d5e' : '#22e0c8', fontWeight: 700 }} />
            )}
          </Box>
          {(!live || !live.bets || live.bets.length === 0) ? (
            <Typography variant="caption" sx={{ color: '#5b6b8c' }}>Ставок в раунде нет</Typography>
          ) : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell sx={{ color: '#8fa1c4' }}>Игрок</TableCell>
                <TableCell sx={{ color: '#8fa1c4' }} align="right">Ставка</TableCell>
                <TableCell sx={{ color: '#8fa1c4' }} align="right">Статус</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {live.bets.map((b, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ color: '#fff' }}>{b.name || 'Игрок'}<DFMark b={b} /></TableCell>
                    <TableCell sx={{ color: '#8fa1c4' }} align="right">{b.bet}</TableCell>
                    <TableCell align="right" sx={{ color: b.cashedOut ? '#2bff9a' : (live.phase === 'crashed' ? '#ff4d5e' : '#8fa1c4'), fontWeight: 700 }}>
                      {b.cashedOut && b.mult != null ? `забрал ${Number(b.mult).toFixed(2)}×` : (live.phase === 'crashed' ? 'не успел' : 'в полёте')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ЛЕНТА последних полётов — только живые игроки */}
      <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,255,255,0.06)', mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Последние полёты</Typography>
          <Typography variant="caption" sx={{ color: '#5b6b8c', display: 'block', mb: 1 }}>
            Только живые игроки. Демо и витрина не показываются. Обновление каждые 15 сек.
          </Typography>
          {flights.length === 0 ? (
            <Typography variant="caption" sx={{ color: '#5b6b8c' }}>Полётов с живыми ставками пока нет</Typography>
          ) : flights.map((f) => (
            <Box key={f.roundId} sx={{ py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', '&:last-child': { borderBottom: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#8fa1c4', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtFlightTime(f.startedAt)}
                </Typography>
                <Chip size="small" label={`${Number(f.crash).toFixed(2)}×`}
                  sx={{ bgcolor: `${crashColor(f.crash)}22`, color: crashColor(f.crash), fontWeight: 700, height: 20 }} />
                <Typography variant="caption" sx={{ color: '#5b6b8c', ml: 'auto' }}>#{f.roundId}</Typography>
              </Box>
              {f.bets.map((b, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, pl: 1, mt: 0.25 }}>
                  <Typography variant="body2" sx={{ color: '#fff', display: 'flex', alignItems: 'center' }}>{b.name}<DFMark b={b} /></Typography>
                  <Typography variant="caption" sx={{ color: '#8fa1c4' }}>ставка {b.bet}</Typography>
                  <Typography variant="caption" sx={{ color: b.status === 'cashed' ? '#2bff9a' : '#8fa1c4' }}>
                    {betResult(b)}
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    color: b.net >= 0 ? '#2bff9a' : '#ff4d5e' }}>
                    {b.net >= 0 ? '+' : ''}{num(b.net)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* ИГРОКИ по периодам — RTP по живым средствам (вагер не учитывается) */}
      <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,255,255,0.06)', mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Игроки</Typography>
            <ToggleButtonGroup size="small" exclusive value={period} onChange={(e, v) => v && setPeriod(v)} sx={{ ml: 1 }}>
              <ToggleButton value="today">Сегодня</ToggleButton>
              <ToggleButton value="7d">7 дней</ToggleButton>
              <ToggleButton value="30d">Месяц</ToggleButton>
              <ToggleButton value="all">Всё время</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={sort} onChange={(e, v) => v && setSort(v)} sx={{ ml: 'auto' }}>
              <ToggleButton value="rtp">по RTP</ToggleButton>
              <ToggleButton value="net">по нетто</ToggleButton>
              <ToggleButton value="bet">по обороту</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Typography variant="caption" sx={{ color: '#5b6b8c', display: 'block', mb: 1 }}>
            RTP — по живым средствам (выигрыш / оборот). Вагер не учитывается.
          </Typography>
          {players.length === 0 ? (
            <Typography variant="caption" sx={{ color: '#5b6b8c' }}>Нет игроков за период</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={{ color: '#8fa1c4' }}>Игрок</TableCell>
                  <TableCell sx={{ color: '#8fa1c4' }} align="right">Ставок</TableCell>
                  <TableCell sx={{ color: '#8fa1c4' }} align="right">Оборот</TableCell>
                  <TableCell sx={{ color: '#8fa1c4' }} align="right">Выигрыш</TableCell>
                  <TableCell sx={{ color: '#8fa1c4' }} align="right">Нетто</TableCell>
                  <TableCell sx={{ color: '#8fa1c4' }} align="right">RTP</TableCell>
                  <TableCell sx={{ color: '#8fa1c4' }} align="right">Лучший×</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {players.map((p) => (
                    <TableRow key={p.userId}>
                      <TableCell sx={{ color: '#fff' }}>{p.name}</TableCell>
                      <TableCell sx={{ color: '#8fa1c4' }} align="right">{p.bets}</TableCell>
                      <TableCell sx={{ color: '#8fa1c4' }} align="right">{num(p.bet)}</TableCell>
                      <TableCell sx={{ color: '#8fa1c4' }} align="right">{num(p.win)}</TableCell>
                      <TableCell align="right" sx={{ color: p.net >= 0 ? '#2bff9a' : '#ff4d5e', fontWeight: 700 }}>
                        {p.net >= 0 ? '+' : ''}{num(p.net)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: p.rtp > 1 ? '#ff7ad1' : '#22e0c8', fontWeight: 700 }}>{pct(p.rtp)}</TableCell>
                      <TableCell sx={{ color: '#f4c34a' }} align="right">{p.bestX != null ? `${p.bestX}×` : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* НЕДЕЛЬНЫЙ ЛИДЕРБОРД — текущая неделя: пул, участники, топы + прогноз призов */}
      <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(244,195,74,0.25)', mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>🏆 Недельный лидерборд — эта неделя</Typography>
            {weekly && <Chip size="small" label={fmtWeek(weekly.weekStart)} sx={{ bgcolor: '#f4c34a22', color: '#f4c34a', fontWeight: 700 }} />}
            {weekly && (
              <Chip size="small"
                label={weekly.willPay ? 'выплата в пн 12:05 МСК' : `пул < мин ${num(weekly.minPool)} — копится`}
                sx={{ ml: 'auto', bgcolor: weekly.willPay ? '#2bff9a22' : '#ff4d5e22', color: weekly.willPay ? '#2bff9a' : '#ff4d5e', fontWeight: 700 }} />
            )}
          </Box>
          {!weekly ? (
            <Typography variant="caption" sx={{ color: '#5b6b8c' }}>Загрузка…</Typography>
          ) : (
            <>
              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={6} md={3}><Stat label="Пул сейчас" value={`${num(weekly.pool)} SPERM`} color="#f4c34a" hint={`${(weekly.cut * 100).toFixed(2)}% оборота недели`} /></Grid>
                <Grid item xs={6} md={3}><Stat label="Участников (оборот)" value={num(weekly.participants.volume)} color="#22e0c8" /></Grid>
                <Grid item xs={6} md={3}><Stat label="Участников (по иксу)" value={num(weekly.participants.mult)} color="#ff7ad1" /></Grid>
                <Grid item xs={6} md={3}><Stat label="Делёж пула" value={`${(weekly.volShare * 100).toFixed(0)}% / ${((1 - weekly.volShare) * 100).toFixed(0)}%`} hint="оборот / икс" /></Grid>
              </Grid>
              <Typography variant="caption" sx={{ color: '#5b6b8c', display: 'block', mb: 1 }}>
                Прогноз призов («+N») — для мест 1–3 каждой номинации, если неделя закроется сейчас.
                Реально начисляет крон в понедельник 12:05 МСК. Демо и фрибеты в зачёт не идут.
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ color: '#22e0c8', fontWeight: 700, display: 'block', mb: 0.5 }}>
                    По обороту — {(weekly.volShare * 100).toFixed(0)}% пула
                  </Typography>
                  {weekly.topVolume.length === 0 ? (
                    <Typography variant="caption" sx={{ color: '#5b6b8c' }}>Нет ставок на этой неделе</Typography>
                  ) : (
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell sx={{ color: '#8fa1c4' }}>#</TableCell>
                        <TableCell sx={{ color: '#8fa1c4' }}>Игрок</TableCell>
                        <TableCell sx={{ color: '#8fa1c4' }} align="right">Оборот</TableCell>
                        <TableCell sx={{ color: '#8fa1c4' }} align="right">Прогноз</TableCell>
                      </TableRow></TableHead>
                      <TableBody>{weekly.topVolume.map((r) => <LbRow key={r.userId} r={r} metric="volume" />)}</TableBody>
                    </Table>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ color: '#ff7ad1', fontWeight: 700, display: 'block', mb: 0.5 }}>
                    По иксу — {((1 - weekly.volShare) * 100).toFixed(0)}% пула
                  </Typography>
                  {weekly.topMult.length === 0 ? (
                    <Typography variant="caption" sx={{ color: '#5b6b8c' }}>Нет забранных ставок</Typography>
                  ) : (
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell sx={{ color: '#8fa1c4' }}>#</TableCell>
                        <TableCell sx={{ color: '#8fa1c4' }}>Игрок</TableCell>
                        <TableCell sx={{ color: '#8fa1c4' }} align="right">Лучший×</TableCell>
                        <TableCell sx={{ color: '#8fa1c4' }} align="right">Прогноз</TableCell>
                      </TableRow></TableHead>
                      <TableBody>{weekly.topMult.map((r) => <LbRow key={r.userId} r={r} metric="mult" />)}</TableBody>
                    </Table>
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>

      {/* НЕДЕЛЬНЫЙ ЛИДЕРБОРД — история финализированных недель + выплаты */}
      <Card sx={{ bgcolor: '#0e1424', border: '1px solid rgba(255,255,255,0.06)', mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}
          onTouchStart={(e) => { histTouch.current = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY }; }}
          onTouchEnd={(e) => {
            if (!history || !history.weeks || history.weeks.length === 0) return;
            const dx = e.changedTouches[0].clientX - histTouch.current.x;
            const dy = e.changedTouches[0].clientY - histTouch.current.y;
            if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
              setHistIdx((i) => dx < 0
                ? Math.min(i + 1, history.weeks.length - 1)   // свайп влево → раньше
                : Math.max(i - 1, 0));                         // свайп вправо → позже
            }
          }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>📜 История лидербордов и выплат</Typography>
          {!history ? (
            <Typography variant="caption" sx={{ color: '#5b6b8c' }}>Загрузка…</Typography>
          ) : history.weeks.length === 0 ? (
            <Typography variant="caption" sx={{ color: '#5b6b8c' }}>Ещё не было ни одной финализации</Typography>
          ) : (
            <>
              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={6} md={4}><Stat label="Всего выплачено" value={`${num(history.totalPaidAllTime)} SPERM`} color="#2bff9a" /></Grid>
                <Grid item xs={6} md={4}><Stat label="Недель сыграно" value={num(history.weeksCount)} /></Grid>
                <Grid item xs={6} md={4}><Stat label="Выплат всего" value={num(history.payoutsCount)} /></Grid>
              </Grid>
              {(() => {
                // Пагинация: одна неделя на странице. 0 = последняя выплаченная,
                // ← (раньше) старее, → (позже) новее. Не захламляем список.
                const idx = Math.min(Math.max(histIdx, 0), history.weeks.length - 1);
                const w = history.weeks[idx];
                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <IconButton size="small" disabled={idx >= history.weeks.length - 1}
                        onClick={() => setHistIdx(idx + 1)} sx={{ color: '#22e0c8' }}><ChevronLeft /></IconButton>
                      <Typography variant="caption" sx={{ color: '#5b6b8c' }}>раньше</Typography>
                      <Chip size="small" label={fmtWeek(w.weekStart)} sx={{ mx: 'auto', bgcolor: '#f4c34a22', color: '#f4c34a', fontWeight: 700 }} />
                      <Typography variant="caption" sx={{ color: '#5b6b8c' }}>позже</Typography>
                      <IconButton size="small" disabled={idx <= 0}
                        onClick={() => setHistIdx(idx - 1)} sx={{ color: '#22e0c8' }}><ChevronRight /></IconButton>
                      <Typography variant="caption" sx={{ color: '#5b6b8c', ml: 1 }}>{idx + 1}/{history.weeks.length}</Typography>
                    </Box>
                    <Box sx={{ mb: 1.5, p: 1, borderRadius: 1, border: '1px solid rgba(255,255,255,0.06)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#8fa1c4' }}>пул {num(w.poolTotal)}</Typography>
                        <Typography variant="caption" sx={{ color: '#2bff9a', fontWeight: 700 }}>выплачено {num(w.totalPaid)}</Typography>
                        <Typography variant="caption" sx={{ color: '#5b6b8c', ml: 'auto' }}>фин. {w.finalizedAt}</Typography>
                      </Box>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead><TableRow>
                            <TableCell sx={{ color: '#8fa1c4' }}>Номинация</TableCell>
                            <TableCell sx={{ color: '#8fa1c4' }}>Место</TableCell>
                            <TableCell sx={{ color: '#8fa1c4' }}>Игрок</TableCell>
                            <TableCell sx={{ color: '#8fa1c4' }} align="right">Результат</TableCell>
                            <TableCell sx={{ color: '#8fa1c4' }} align="right">Выплата</TableCell>
                          </TableRow></TableHead>
                          <TableBody>
                            {w.payouts.map((p, i) => (
                              <TableRow key={i}>
                                <TableCell sx={{ color: p.category === 'volume' ? '#22e0c8' : '#ff7ad1' }}>
                                  {p.category === 'volume' ? 'оборот' : 'икс'}
                                </TableCell>
                                <TableCell sx={{ color: '#f4c34a', fontWeight: 700 }}>{medal(p.place)}</TableCell>
                                <TableCell sx={{ color: '#fff' }}>{p.name}</TableCell>
                                <TableCell align="right" sx={{ color: '#8fa1c4' }}>
                                  {p.category === 'mult' ? `${Number(p.metricValue).toFixed(2)}×` : num(p.metricValue)}
                                </TableCell>
                                <TableCell align="right" sx={{ color: '#2bff9a', fontWeight: 700 }}>+{num(p.amount)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Box>
                  </>
                );
              })()}
            </>
          )}
        </CardContent>
      </Card>

    </Box>
  );
}
